import { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, Loader2, AlertTriangle, Video, Phone, Building2, MapPin } from "lucide-react";
import api from "@/lib/api";

/**
 * Stripe Checkout success-return page.
 *
 * Stripe redirects here with `?session_id=cs_test_…`. We poll
 * /api/booking/public/payment/status/{session_id} every 2s until the
 * backend confirms the booking (paid + confirmed). Polling backs off
 * after 30 attempts (~60s) — at that point the webhook should have
 * picked it up anyway, but we show a graceful timeout state with a
 * "check your email" message.
 */
export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const navigate = useNavigate();
  const [state, setState] = useState({ status: "polling", payment_status: "awaiting_payment", attempt: 0 });
  const [result, setResult] = useState(null);
  const timerRef = useRef(null);

  const poll = useCallback(async (attempt) => {
    if (!sessionId) return;
    try {
      const r = await api.get(`/booking/public/payment/status/${encodeURIComponent(sessionId)}`);
      const data = r.data || {};
      if (data.payment_status === "paid") {
        setResult(data);
        setState({ status: "paid", payment_status: "paid", attempt });
        return;
      }
      if (data.payment_status === "failed" || data.status === "canceled") {
        setState({ status: "failed", payment_status: "failed", attempt });
        return;
      }
      if (attempt >= 30) {
        setState({ status: "timeout", payment_status: "awaiting_payment", attempt });
        return;
      }
      setState({ status: "polling", payment_status: "awaiting_payment", attempt });
      timerRef.current = setTimeout(() => poll(attempt + 1), 2000);
    } catch {
      if (attempt >= 30) {
        setState({ status: "timeout", payment_status: "unknown", attempt });
        return;
      }
      timerRef.current = setTimeout(() => poll(attempt + 1), 3000);
    }
  }, [sessionId]);

  useEffect(() => {
    if (!sessionId) {
      setState({ status: "missing_session", attempt: 0 });
      return;
    }
    poll(0);
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [sessionId, poll]);

  if (state.status === "missing_session") {
    return (
      <Shell>
        <div className="text-center" data-testid="pay-success-missing">
          <AlertTriangle className="w-12 h-12 mx-auto text-[#b45309] mb-3" />
          <h1 className="bloq-headline text-3xl mb-2">We couldn't find your payment session.</h1>
          <p className="text-sm text-[#4a4a52]">If you completed payment, check your email for confirmation. Otherwise please try booking again.</p>
        </div>
      </Shell>
    );
  }

  if (state.status === "polling") {
    return (
      <Shell>
        <div className="text-center" data-testid="pay-success-polling">
          <Loader2 className="w-12 h-12 mx-auto text-[#005e7a] mb-3 animate-spin" />
          <h1 className="bloq-headline text-3xl mb-2">Finalizing your booking…</h1>
          <p className="text-sm text-[#4a4a52]">We're confirming your payment with Stripe. This usually takes a few seconds.</p>
        </div>
      </Shell>
    );
  }

  if (state.status === "timeout") {
    return (
      <Shell>
        <div className="text-center" data-testid="pay-success-timeout">
          <AlertTriangle className="w-12 h-12 mx-auto text-[#b45309] mb-3" />
          <h1 className="bloq-headline text-3xl mb-2">Payment is processing</h1>
          <p className="text-sm text-[#4a4a52]">
            Your payment is taking a little longer than expected. We'll send a confirmation email as soon as it lands.
            You can safely close this window — nothing else is required from you.
          </p>
        </div>
      </Shell>
    );
  }

  if (state.status === "failed") {
    return (
      <Shell>
        <div className="text-center" data-testid="pay-success-failed">
          <AlertTriangle className="w-12 h-12 mx-auto text-[#b45309] mb-3" />
          <h1 className="bloq-headline text-3xl mb-2">Your payment couldn't be completed.</h1>
          <p className="text-sm text-[#4a4a52]">
            Please try booking again or contact the host directly. No charge was made to your card.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="bloq-btn-primary mt-5"
            data-testid="pay-success-retry"
          >
            Go back
          </button>
        </div>
      </Shell>
    );
  }

  // state.status === "paid"
  const startLocal = result?.start_time_utc
    ? new Date(result.start_time_utc).toLocaleString(undefined, {
        weekday: "long", month: "long", day: "numeric",
        hour: "numeric", minute: "2-digit",
      })
    : "";
  const joinUrl = result?.meeting_public_id ? `/m/${result.meeting_public_id}` : null;

  return (
    <Shell>
      <div className="text-center" data-testid="pay-success-paid">
        <CheckCircle2 className="w-12 h-12 mx-auto text-[#15803d] mb-3" />
        <h1 className="bloq-headline text-3xl mb-2">Payment received — you're booked!</h1>
        <p className="text-sm text-[#4a4a52] mb-6">
          {result?.appointment_type_name ? <strong>{result.appointment_type_name}</strong> : "Your booking"} {startLocal ? `· ${startLocal}` : ""}
        </p>
        {result?.confirmation_message && (
          <div
            className="bg-[#f5efe6] p-4 rounded-lg text-left text-sm text-[#4a4a52] mb-6"
            data-testid="pay-success-confirmation-message"
          >
            {result.confirmation_message}
          </div>
        )}
        {joinUrl && (
          <a
            href={joinUrl}
            className="bloq-btn-primary inline-flex items-center gap-2"
            data-testid="pay-success-join"
          >
            <Video className="w-4 h-4" /> Join meeting room
          </a>
        )}
        <p className="text-xs text-[#82828c] mt-6">
          A confirmation email is on its way. You can close this window.
        </p>
      </div>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-10 bg-white">
      <div className="bloq-card p-10 max-w-xl w-full">{children}</div>
    </div>
  );
}
